import { App, type EnvironmentWithChina } from '@susu/customer-hub'
import { ChinaCdnFonts } from '@susu/customer-hub/components/ChinaCdnFonts'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { buildEntraAuthUrl } from './utils/entraAuth'
import { getRole } from './utils/getRole'
import {
  RETURN_TARGET_STATE_KEY,
  resolveReturnTarget,
} from './utils/returnTarget'
import { verifyToken } from './utils/validateToken'

import '../../../packages/customer-hub/src/styles/index.css'
import '../../../packages/ui/src/styles/InputPhoneNumber.css'

const rootElement = document.getElementById('root')
const ACCESS_TOKEN_STORAGE_KEY = 'acl-token'
const CUSTOMER_PROFILE_EMAIL_QUERY_PARAM = 'customerProfileEmail'

if (!rootElement) {
  throw new Error('Root element not found')
}
const appRoot: HTMLElement = rootElement
const root = createRoot(appRoot)

// Get environment from Vite env variable or default to 'tst'
const env = (import.meta.env.VITE_ENV as EnvironmentWithChina) || 'tst'
// Customer Hub owns this page: the RP shell renders nothing else that tracks,
// so this mount loads the transport. The Segment source and the consent
// posture follow from Customer Hub's in-store placement, so there is no
// OneTrust id or GA cookie to plumb through any more.
const analyticsConfig = { ownsPage: true }

function parseStateFromHash(
  state: string | null,
): Record<string, string | number | boolean> {
  if (!state) {
    return {}
  }

  try {
    const decodedState = decodeURIComponent(state)
    const cleanState = decodedState.replace(/^['"]|['"]$/g, '')
    return JSON.parse(cleanState) as Record<string, string | number | boolean>
  } catch (error) {
    console.error('Failed to parse state from Entra callback:', error)
    return {}
  }
}

function applyStateToUrl(
  url: URL,
  stateObject: Record<string, string | number | boolean>,
): void {
  Object.entries(stateObject).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      url.searchParams.set(key, String(value))
    }
  })
}

function handleCallbackHash(): boolean {
  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash

  if (!hash) {
    return false
  }

  const params = new URLSearchParams(hash)
  const accessToken = params.get('access_token')

  if (!accessToken) {
    return false
  }

  const state = parseStateFromHash(params.get('state'))
  const returnTarget = resolveReturnTarget(
    state[RETURN_TARGET_STATE_KEY],
    window.location.origin,
    env,
  )
  delete state[RETURN_TARGET_STATE_KEY]

  // Entra returns to the registered host (testing) whichever deployment started
  // the sign-in. Hand the callback on to that deployment, which stores the token
  // itself; this host keeps nothing.
  if (returnTarget.origin !== window.location.origin) {
    window.location.replace(
      `${returnTarget.origin}${returnTarget.pathname}${window.location.hash}`,
    )
    return true
  }

  sessionStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, accessToken)

  // Clear the callback hash and restore path and state params after successful auth.
  const redirectUrl = new URL(returnTarget.pathname, window.location.origin)
  applyStateToUrl(redirectUrl, state)
  window.location.replace(redirectUrl.toString())
  return true
}

async function ensureAuthenticated(): Promise<boolean> {
  // Entra implicit-flow callback: capture hash token and clean URL.
  if (handleCallbackHash()) {
    return false
  }

  const token = sessionStorage.getItem(ACCESS_TOKEN_STORAGE_KEY)

  if (!token) {
    const entraAuthUrl = buildEntraAuthUrl(
      new URLSearchParams(window.location.search),
    )
    window.location.replace(entraAuthUrl)
    return false
  }

  const isValidToken = await verifyToken(token)
  if (!isValidToken) {
    sessionStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY)
    const entraAuthUrl = buildEntraAuthUrl(
      new URLSearchParams(window.location.search),
    )
    window.location.replace(entraAuthUrl)
    return false
  }

  return true
}

function getStoredAccessToken(): string {
  return sessionStorage.getItem(ACCESS_TOKEN_STORAGE_KEY) || ''
}

function getQueryParamPreservingPlus(paramName: string): string {
  const rawQuery = window.location.search.startsWith('?')
    ? window.location.search.slice(1)
    : window.location.search
  if (!rawQuery) {
    return ''
  }

  const queryPairs = rawQuery.split('&')
  for (const pair of queryPairs) {
    if (!pair) {
      continue
    }

    const separatorIndex = pair.indexOf('=')
    const rawKey = separatorIndex >= 0 ? pair.slice(0, separatorIndex) : pair
    const rawValue = separatorIndex >= 0 ? pair.slice(separatorIndex + 1) : ''

    let decodedKey = rawKey
    try {
      decodedKey = decodeURIComponent(rawKey)
    } catch {
      // Keep raw key when decode fails.
    }

    if (decodedKey !== paramName) {
      continue
    }

    try {
      return decodeURIComponent(rawValue)
    } catch {
      return rawValue
    }
  }

  return ''
}

function getCustomerProfileEmail(): string | undefined {
  const email = getQueryParamPreservingPlus(CUSTOMER_PROFILE_EMAIL_QUERY_PARAM)

  const normalizedEmail = email.trim()
  if (!normalizedEmail) {
    return undefined
  }

  return normalizedEmail
}

async function bootstrap(): Promise<void> {
  root.render(
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <h2>Authenticating...</h2>
        <p>Please wait while we complete your sign-in.</p>
      </div>
    </div>,
  )

  let isAuthenticated = false
  try {
    isAuthenticated = await ensureAuthenticated()
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Authentication setup failed.'

    root.render(
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          padding: '24px',
        }}
      >
        <div style={{ textAlign: 'center', maxWidth: '640px' }}>
          <h2>Authentication configuration error</h2>
          <p>{message}</p>
        </div>
      </div>,
    )
    return
  }

  if (!isAuthenticated) {
    return
  }

  root.render(
    <StrictMode>
      <ChinaCdnFonts env={env}>
        <App
          env={env}
          authToken={getStoredAccessToken()}
          customerProfileEmail={getCustomerProfileEmail()}
          role={getRole()}
          context="standalone"
          analyticsConfig={analyticsConfig}
        />
      </ChinaCdnFonts>
    </StrictMode>,
  )
}

void bootstrap()
